// library imports
import { useState, useEffect } from 'react';
import { useNavigate, useLocation  } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

// User Component imports
import { Button, Icon, Stack, Row } from "_core/components/common/UI";
import * as SceneConfig from "_core/configs/SceneConfig";
import { deviceType, useDevice } from '_core/managers/ResponsiveManager';
import { setMenuState, setInitializeState } from '_core/slices/scene/SceneSlice';
import * as Constants from "_core/configs/Constants";
import * as LocalStorageUtils from "_core/utils/localstorage";

// styles imports
import './Menu.scss';

function GetNavigatePath(destination){
  return Constants.REACT_APP_BASE_PATH + "/" + destination.join("/")
}

// メインのコンポーネント定義
export default function Menu(props) {
  const { scene, parts, isMenu, isMenuOpen } = useSelector((store) => store.scene);
  const { initializeState } = useSelector((store) => store.scene);
  const { tUser, tAccount, tAppearanceList } = useSelector((store) => store.transaction);
  const device = useDevice();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [isAppearanceEmpty, setIsAppearanceEmpty] = useState(true);
  const [logo, setLogo] = useState(Constants.REACT_APP_BASE_PATH + `/img/PRizmo_rogo_big.png`);

  // メニューの開閉状態に応じてCSS変数を更新
  useEffect(() => {
    const menuWidth = isMenuOpen ? '240px' : '64px';
    document.documentElement.style.setProperty('--menu-current-width', menuWidth);
  }, [isMenuOpen]);

  // サービスタイプで画像切り替え
  useEffect(() => {
    if (!tUser){
      return;
    }

    setLogo(Constants.REACT_APP_BASE_PATH + `/img/PRizmo_rogo_big.png`)
  }, [tUser]);

  // tAppearanceの変化を監視
  useEffect(() => {
    // tAppearanceが存在し、かつ空でないかをチェック
    const isEmpty = !tAppearanceList || Object.keys(tAppearanceList).length === 0;
    setIsAppearanceEmpty(isEmpty);
  }, [tAppearanceList]);

  const onChangeMenu = () => {
    dispatch(setMenuState({ isMenuOpen: !isMenuOpen }));
  };

  const onClickSceneButton = (destination) => {
    dispatch(setInitializeState(Constants.INITIALIZE_STATE.ready))
    navigate(GetNavigatePath(destination));
  };

  if (! isMenu && initializeState > Constants.INITIALIZE_STATE.allComplete)
    return (
      <Stack className="menu-root">
      </Stack>
    );
  
  // 子要素のコンポーネント定義
  function MenuItem(props) {
    const location = useLocation();

    let isSelected = (GetNavigatePath(props.destination) === location.pathname);
    if (props.groupPage)
    {
      props.groupPage.forEach(element => {
        if (location.pathname == GetNavigatePath(element))
        {
          isSelected = true;
        }
      });
    }
    return (
      <div className={"menu-item " + (isSelected ? "selected" : "") + (props.disabled ? " disabled" : "")}>
        <Button
          className="hidden flex flex-both no-padding"
          onClick={() => props.onClick(props.destination)}
          disabled={props.disabled}
        >
          <div className="menu-item-icon-area padding-12px flex flex-center">
            <Icon icon={props.icon} isIconFont={props.isIconFont} size={props.isIconFont ? 20 : 28} />
          </div>
          <div className="padding-12px">{props.title}</div>
        </Button>
      </div>
    );
  }

  // 子要素のコンポーネント定義
  function MenuGroup(props) {
    const location = useLocation();
    let isSelected = false;
    if (props.groupPage)
    {
      props.groupPage.forEach(element => {
        if (location.pathname == GetNavigatePath(element))
        {
          isSelected = true;
        }
      });
    }
    const [isGroupOpen, setIsGroupOpen] = useState(isSelected || LocalStorageUtils.GetItem("openMenuGroup") == props.groupName);
  
    return (
      <div className={"menu-group " + (isGroupOpen ? " group-open" : "")}> 
        <Button className="hidden flex flex-both no-padding" onClick={() => {
            setIsGroupOpen(!isGroupOpen);
            LocalStorageUtils.SetItem("openMenuGroup", !isGroupOpen ? props.groupName : "");
        }}> 
          <div className="menu-item-icon-area padding-12px flex flex-center">
            <Icon className="group-icon" icon="caret-right-fill" isIconFont={true} size={18} />
          </div>
          <div className="padding-12px">{props.title}</div>
        </Button>
        <Stack className="menu-group-list">
          {props.children}
        </Stack>
      </div>
    );
  }
  
  return (
    <div className={"menu-drawer " + (isMenuOpen && "open shadow-ease")}>
      <Stack className="flex-both height-full">
        <Stack>
          {! (device === deviceType.mobile.type ) && 
            <div className="menu-header">
              <Row className={"align-center " + (isMenuOpen ? "flex-both" : "flex-center")}>
                <img src={logo} alt="" className="logo"/>
                <Button className={"menu-switch-button hidden flex align-center " + (isMenuOpen ? "flex-right" : "flex-center")} onClick={onChangeMenu}> 
                    <Icon icon={isMenuOpen ? "chevron-left" : "chevron-right"} size={16} />
                </Button>
              </Row>
            </div>
          }
          <div className="menu-list">
              <MenuItem title="基本情報"   isText={isMenuOpen} icon="info" isIconFont={false}               onClick={onClickSceneButton} destination={[SceneConfig.SCENE_ADMIN, SceneConfig.PARTS_ADMIN_USEREDIT]}></MenuItem>
              {/* <MenuItem title="表示設定"   isText={isMenuOpen} icon="display" isIconFont={false}              onClick={onClickSceneButton} destination={[SceneConfig.SCENE_ADMIN, SceneConfig.PARTS_ADMIN_USER_PROFILE_EDIT]} disabled={isAppearanceEmpty}></MenuItem> */}
              <MenuItem title="データ構造化"      isText={isMenuOpen} icon="grid-1x2"             isIconFont={true}      onClick={onClickSceneButton} destination={[SceneConfig.SCENE_ADMIN, SceneConfig.PARTS_ADMIN_USER_RAG_STRUCTURE]} ></MenuItem>
              <MenuItem title="RAGファイル管理"   isText={isMenuOpen} icon="file-earmark-text"    isIconFont={true}      onClick={onClickSceneButton} destination={[SceneConfig.SCENE_ADMIN, SceneConfig.PARTS_ADMIN_USER_RAG_UPLOAD]} ></MenuItem>
              <MenuItem title="RAG応答テスト"     isText={isMenuOpen} icon="check-all"            isIconFont={true}      onClick={onClickSceneButton} destination={[SceneConfig.SCENE_ADMIN, SceneConfig.PARTS_ADMIN_USER_RAG_EVALUATE]} ></MenuItem>
              <MenuItem title="Playground"       isText={isMenuOpen} icon="chat-left-dots"       isIconFont={true}      onClick={onClickSceneButton} destination={[SceneConfig.SCENE_ADMIN, SceneConfig.PARTS_ADMIN_USER_RAG_PLAYGROUND]} ></MenuItem>
              <MenuItem title="ログ"               isText={isMenuOpen} icon="log"                   isIconFont={false}     onClick={onClickSceneButton} destination={[SceneConfig.SCENE_ADMIN, SceneConfig.PARTS_ADMIN_USER_CHATLOG]}></MenuItem>
              <MenuItem title="レポート"           isText={isMenuOpen} icon="file-earmark-text"     isIconFont={true}     onClick={onClickSceneButton} destination={[SceneConfig.SCENE_ADMIN, SceneConfig.PARTS_ADMIN_USER_CHATREPORT]}></MenuItem>
              <MenuItem title="WelcomeMessage"   isText={isMenuOpen} icon="person-vcard"     isIconFont={true}     onClick={onClickSceneButton} destination={[SceneConfig.SCENE_ADMIN, SceneConfig.PARTS_ADMIN_WELCOME_MSG_EDIT]}></MenuItem>
              <MenuItem title="HRニュース"        isText={isMenuOpen} icon="newspaper"            isIconFont={true}     onClick={onClickSceneButton} destination={[SceneConfig.SCENE_ADMIN, SceneConfig.PARTS_ADMIN_HR_NEWS_EDIT]}></MenuItem>
          </div>
        </Stack>
          <Stack className="edge-group align-center">
            {isMenuOpen ? 
              <div className="logout-item"> 
                <Button className="hidden flex flex-both no-padding" onClick={() => {
                  onClickSceneButton([SceneConfig.SCENE_ACCOUNT, SceneConfig.PARTS_TOP])
                }}>
                  <div className="logout-item-icon-area padding-12px flex flex-center">
                    <Icon icon="logout" isIconFont={false} size={24} />
                  </div>
                  <div className="logout-label padding-12px">ログアウト</div>
                </Button>
              </div>
              :
              <div className="logout-item shrink"> 
                <Button className="hidden flex flex-both no-padding" onClick={() => {
                  onClickSceneButton([SceneConfig.SCENE_ACCOUNT, SceneConfig.PARTS_TOP])
                }}>
                  <div className="logout-item-icon-area padding-12px flex flex-center">
                    <Icon icon="logout" isIconFont={false} size={24} />
                  </div>
                </Button>
              </div>
            }
          </Stack>
      </Stack>
    </div>
  );
}

// 子要素のコンポーネント定義

function MenuItem(props) {
  const location = useLocation();

  let isSelected = (GetNavigatePath(props.destination) === location.pathname);
  if (props.groupPage)
  {
    props.groupPage.forEach(element => {
      if (location.pathname == GetNavigatePath(element))
      {
        isSelected = true;
      }
    });
  }
  return (
    <div className={"menu-item " + (isSelected ? "selected" : "") + (props.disabled ? " disabled" : "")}> 
      <Button
        className="hidden flex flex-both no-padding"
        onClick={() => props.onClick(props.destination)}
        disabled={props.disabled}
      >
        <div className="menu-item-icon-area padding-12px flex flex-center">
          <Icon icon={props.icon} isIconFont={false} size={28} />
        </div>
        <div className="padding-12px">{props.title}</div>
      </Button>
    </div>
  );
}
